import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { ProductCard } from '../components/ProductCard';
import { collectionsAPI, productsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

export const CollectionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCollection();
  }, [id]);

  const loadCollection = async () => {
    try {
      const collectionRes = await collectionsAPI.getAll();
      const foundCollection = collectionRes.data.find(c => c.id === id);
      
      if (!foundCollection) {
        toast.error('Collection not found');
        navigate('/collections');
        return;
      }
      
      setCollection(foundCollection);

      // Load products in this collection
      const productsRes = await productsAPI.getAll({});
      const collectionProducts = productsRes.data.filter(p => 
        foundCollection.product_ids.includes(p.id)
      );
      setProducts(collectionProducts);
    } catch (error) {
      toast.error('Failed to load collection');
      navigate('/collections');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-consortium-green"></div>
        </div>
      </div>
    );
  }

  if (!collection) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/collections')}
          className="mb-6 gap-2"
          data-testid="back-to-collections"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Collections
        </Button>

        <div className="mb-8">
          <h1 className="font-barlow text-4xl font-bold text-slate-900 mb-3" data-testid="collection-name">
            {collection.name}
          </h1>
          <p className="text-slate-600 text-lg">{collection.description}</p>
          <p className="text-sm text-slate-500 mt-2">{products.length} products in this collection</p>
        </div>

        {products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="collection-products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <h3 className="font-barlow text-xl font-semibold text-slate-900 mb-2">No products in this collection</h3>
            <p className="text-slate-600">Products will appear here once added</p>
          </div>
        )}
      </div>
    </div>
  );
};
